import { useEffect, useRef, useState } from 'react';

import { v4 as uuid } from 'uuid';
import dayjs from '~/timeInit';

import {
  type SelectedVoucherUser,
  createVoucherUserBulkBatches,
  createVoucherUserExpiryBatches,
} from '~/components/Vouchers/voucherUserBulkBatches';

import useCurrentBrand from '~/hooks/useCurrentBrand';

import { type ListAllVoucherUsersQuery, type UpdateVoucherUsersExpiryByIdsPayload } from '~/services/PromoService';

import { normalizeToISODate } from '~/utils/dateFormatter';

import { useBatchedBulkDeleteVoucherUsers } from './useBulkDeleteVoucherUsers';
import { useLoadMatchingVoucherUsers } from './useLoadMatchingVoucherUsers';
import { useUpdateVoucherUsersExpiryByIds } from './useUpdateVoucherUsersExpiryByIds';

type BatchStatus = 'queued' | 'loading' | 'previewed' | 'success' | 'failed';
export type VoucherUserBulkBatch = {
  id: number;
  voucherId: string;
  userIds: Array<string>;
  voucherUserIds: Array<string>;
  eligibleUserIds: Array<string>;
  skippedCount: number;
  processedCount: number;
  status: BatchStatus;
  error?: string;
};

type Options = {
  defaultExpiryDate?: string;
  matchingQuery: ListAllVoucherUsersQuery;
  matchingCount: number;
  matchingReady: boolean;
  hasMatchingFilters: boolean;
  selected: Array<SelectedVoucherUser>;
  batchSize: number;
  onFinished: () => Promise<void>;
  onSelectionLockChange: (locked: boolean) => void;
};

function createBatches(selected: Array<SelectedVoucherUser>, batchSize: number): Array<VoucherUserBulkBatch> {
  return createVoucherUserBulkBatches(selected, batchSize).map((batch, index) => ({
    ...batch,
    id: index + 1,
    voucherUserIds: [],
    eligibleUserIds: [],
    skippedCount: 0,
    processedCount: 0,
    status: 'queued',
  }));
}

export function useVoucherUserBulkOperation({
  defaultExpiryDate = '',
  selected,
  matchingQuery,
  matchingCount,
  matchingReady,
  hasMatchingFilters,
  batchSize,
  onFinished,
  onSelectionLockChange,
}: Options) {
  const brand = useCurrentBrand();
  const [tagAdd, setTagAdd] = useState('');
  const [tagRemove, setTagRemove] = useState('');
  const [reviewedBrand, setReviewedBrand] = useState<UpdateVoucherUsersExpiryByIdsPayload['brand'] | null>(null);
  const [reviewedTags, setReviewedTags] = useState({ add: '', remove: '' });
  const updateExpiryMutation = useUpdateVoucherUsersExpiryByIds();
  const [expiryDate, setExpiryDate] = useState(defaultExpiryDate);
  useEffect(() => {
    setExpiryDate(defaultExpiryDate);
  }, [defaultExpiryDate]);
  const [reviewedExpiry, setReviewedExpiry] = useState('');
  const expiryIso = expiryDate ? normalizeToISODate(expiryDate) : '';
  const isExpiryValid = Boolean(expiryDate && dayjs(expiryIso).isValid() && dayjs(expiryIso).isAfter(dayjs()));
  const loadMatchingMutation = useLoadMatchingVoucherUsers();
  const [scope, setScope] = useState<'selected' | 'matching'>('selected');
  const [reviewedCount, setReviewedCount] = useState(0);
  const [loadedCount, setLoadedCount] = useState(0);
  const deleteMutation = useBatchedBulkDeleteVoucherUsers();
  const [action, setAction] = useState<'delete' | 'expiry'>('delete');
  const [batches, setBatches] = useState<Array<VoucherUserBulkBatch>>([]);
  const [phase, setPhase] = useState<'idle' | 'previewing' | 'reviewing' | 'executing'>('idle');
  const operationActive = useRef(false);
  const isRunning = phase === 'previewing' || phase === 'executing';
  const confirmOpen = phase === 'reviewing';
  const [error, setError] = useState('');
  const [completed, setCompleted] = useState(false);
  const [refreshError, setRefreshError] = useState('');

  useEffect(() => {
    if (selected.length === 0 && phase === 'idle' && scope === 'selected' && !completed) {
      setBatches([]);
      setError('');
      setCompleted(false);
      setRefreshError('');
      setPhase('idle');
    }
  }, [selected.length, phase, scope, completed]);

  const eligibleCount = batches.reduce(
    (count, batch) => count + (action === 'expiry' ? batch.voucherUserIds.length : batch.eligibleUserIds.length),
    0,
  );
  const processedCount = batches.reduce((count, batch) => count + batch.processedCount, 0);
  const skippedCount = batches.reduce((count, batch) => count + batch.skippedCount, 0);

  const updateBatch = (id: number, update: Partial<VoucherUserBulkBatch>) => {
    setBatches((current) => current.map((batch) => (batch.id === id ? { ...batch, ...update } : batch)));
  };

  const preview = async (nextScope: 'selected' | 'matching') => {
    if (operationActive.current || confirmOpen) return;
    if (nextScope === 'matching' && (!matchingReady || matchingCount === 0)) return;
    if (nextScope === 'matching' && !hasMatchingFilters) return;
    if (action === 'expiry' && !isExpiryValid) return;
    if (action === 'expiry' && tagAdd.trim() && tagAdd.trim() === tagRemove.trim()) {
      setError('The tag to add and remove must differ.');
      return;
    }
    setReviewedTags({ add: tagAdd.trim(), remove: tagRemove.trim() });
    setReviewedExpiry(expiryIso);
    setScope(nextScope);
    setError('');
    setRefreshError('');
    setCompleted(false);
    setBatches([]);
    setLoadedCount(0);
    setReviewedCount(nextScope === 'matching' ? matchingCount : selected.length);
    operationActive.current = true;
    setPhase('previewing');
    onSelectionLockChange(true);
    let activeBatchId: number | undefined;
    const batchId = uuid();
    try {
      const snapshot =
        nextScope === 'matching'
          ? await loadMatchingMutation.mutateAsync({
              query: { ...matchingQuery },
              total: matchingCount,
              onProgress: setLoadedCount,
            })
          : [...selected];
      setReviewedCount(snapshot.length);
      const planned: Array<VoucherUserBulkBatch> =
        action === 'expiry'
          ? createVoucherUserExpiryBatches(snapshot, 500).map((batch, index) => ({
              ...batch,
              id: index + 1,
              userIds: [],
              eligibleUserIds: [],
              skippedCount: 0,
              processedCount: 0,
              status: 'previewed' as const,
            }))
          : createBatches(snapshot, batchSize);
      setBatches([...planned]);
      if (planned.length === 0) {
        setError('No matching records are available. Refresh the list and try again.');
        onSelectionLockChange(false);
        return;
      }
      if (action === 'expiry') {
        if (brand === 'yidu' || brand === 'newwhitelabel')
          throw new Error('Voucher updates are unavailable for this brand.');
        setReviewedBrand(brand);
        for (let index = 0; index < planned.length; index += 1) {
          const batch = planned[index];
          activeBatchId = batch.id;
          updateBatch(batch.id, { status: 'loading' });
          const response = await updateExpiryMutation.mutateAsync({
            voucherUserIds: batch.voucherUserIds,
            brand,
            bulkUpdatePayload: {
              expiredAt: expiryIso,
              voucher_reporting_tag_add: tagAdd.trim() || undefined,
              voucher_reporting_tag_remove: tagRemove.trim() || undefined,
            },
            dryRun: true,
          });
          if (response.result.eligibleIds.some((id) => !batch.voucherUserIds.includes(id))) {
            throw new Error('Expiry preview returned assignments outside the requested batch.');
          }
          planned[index] = {
            ...batch,
            voucherUserIds: response.result.eligibleIds,
            skippedCount: response.result.skippedIds.length + response.result.notFoundIds.length,
            status: 'previewed',
          };
          updateBatch(batch.id, planned[index]);
        }
        setPhase('reviewing');
        return;
      }
      for (let index = 0; index < planned.length; index += 1) {
        const batch = planned[index];
        activeBatchId = batch.id;
        updateBatch(batch.id, { status: 'loading' });
        const response = await deleteMutation.mutateAsync({
          voucher_id: batch.voucherId,
          voucher_users: batch.userIds.map((user_id) => ({ user_id })),
          dryRun: true,
          batchId,
          batchRunCountTotal: planned.length,
          batchRunCount: batch.id,
        });
        if (response.result.willDelete.some((row) => !batch.userIds.includes(row.userId))) {
          throw new Error('The deletion preview returned records outside the requested batch.');
        }
        const previewed: VoucherUserBulkBatch = {
          ...batch,
          eligibleUserIds: response.result.willDelete.map((user) => user.userId),
          skippedCount: response.result.skipped.length,
          status: 'previewed',
        };
        planned[index] = previewed;
        updateBatch(batch.id, previewed);
      }
      setPhase('reviewing');
    } catch (cause) {
      const message = cause instanceof Error ? cause.message : 'Preview failed';
      if (activeBatchId !== undefined) updateBatch(activeBatchId, { status: 'failed', error: message });
      setError(`Preview stopped: ${message}`);
      onSelectionLockChange(false);
    } finally {
      operationActive.current = false;
      setPhase((current) => (current === 'previewing' ? 'idle' : current));
    }
  };

  const execute = async () => {
    if (operationActive.current || !confirmOpen || eligibleCount === 0) return;
    if (action === 'expiry' && (!reviewedBrand || reviewedBrand !== brand)) {
      setError('The selected brand changed. Cancel and preview again.');
      return;
    }
    setError('');
    setRefreshError('');
    operationActive.current = true;
    setPhase('executing');
    const executable = batches.filter((batch) =>
      action === 'expiry' ? batch.voucherUserIds.length > 0 : batch.eligibleUserIds.length > 0,
    );
    const batchId = uuid();
    let completedCount = 0;
    let stopped = false;
    try {
      for (let index = 0; index < executable.length; index += 1) {
        const batch = executable[index];
        updateBatch(batch.id, { status: 'loading' });
        try {
          if (action === 'expiry') {
            const response = await updateExpiryMutation.mutateAsync({
              voucherUserIds: batch.voucherUserIds,
              brand: reviewedBrand,
              bulkUpdatePayload: {
                expiredAt: reviewedExpiry,
                voucher_reporting_tag_add: reviewedTags.add || undefined,
                voucher_reporting_tag_remove: reviewedTags.remove || undefined,
              },
              dryRun: false,
            });
            updateBatch(batch.id, {
              status: 'success',
              processedCount: response.result.updatedIds.length,
              skippedCount: batch.skippedCount + response.result.skippedIds.length + response.result.notFoundIds.length,
            });
            completedCount += response.result.updatedIds.length;
            continue;
          }
          const response = await deleteMutation.mutateAsync({
            voucher_id: batch.voucherId,
            voucher_users: batch.eligibleUserIds.map((user_id) => ({ user_id })),
            dryRun: false,
            batchId,
            batchRunCountTotal: executable.length,
            batchRunCount: index + 1,
          });
          updateBatch(batch.id, {
            status: 'success',
            processedCount: response.result.deletedVoucherUsers.length,
            skippedCount: batch.skippedCount + response.result.skipped.length,
          });
          completedCount += response.result.deletedVoucherUsers.length;
        } catch (cause) {
          const message = cause instanceof Error ? cause.message : 'Bulk operation failed';
          updateBatch(batch.id, { status: 'failed', error: message });
          setError(
            `${action === 'expiry' ? 'Expiry update' : 'Deletion'} stopped on batch ${index + 1} of ${executable.length} after processing ${completedCount} voucher users: ${message}`,
          );
          stopped = true;
          break;
        }
      }
    } finally {
      setCompleted(!stopped);
      try {
        await onFinished();
      } catch (cause) {
        setRefreshError(
          `The list could not refresh: ${cause instanceof Error ? cause.message : 'Unknown error'}. Refresh the list before making further changes.`,
        );
      }
      onSelectionLockChange(false);
      operationActive.current = false;
      setPhase('idle');
    }
  };

  const cancel = () => {
    if (operationActive.current) return;
    setPhase('idle');
    onSelectionLockChange(false);
  };

  const changeAction = (nextAction: 'delete' | 'expiry') => {
    if (operationActive.current || confirmOpen) return;
    setAction(nextAction);
    setBatches([]);
    setCompleted(false);
    setError('');
    setRefreshError('');
  };

  return {
    action,
    changeAction,
    tagAdd,
    setTagAdd,
    tagRemove,
    setTagRemove,
    expiryDate,
    setExpiryDate,
    isExpiryValid,
    scope,
    reviewedCount,
    loadedCount,
    reviewedExpiry,
    reviewedTags,
    batches,
    isRunning,
    confirmOpen,
    error,
    completed,
    refreshError,
    eligibleCount,
    processedCount,
    skippedCount,
    isLoadingMatching: loadMatchingMutation.isPending,
    preview,
    execute,
    cancel,
  };
}
